import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { priceCart, type CartLineInput, type TaxConfig, type PricedCart } from "../../domain/pricing/price-cart";
import {
  lineDiscountParts,
  splitLineForGrant,
  withLineDiscounts,
  GrantError,
  type LineDiscountPart,
} from "../../domain/pricing/line-grant";
import { computeStatutoryDiscount, type StatutoryDiscountResult } from "../../domain/pricing/statutory";
import { sum } from "../../domain/money/money";
import { modules } from "../../platform/registry/registry";
import type { CatalogItemView } from "../../data/local/catalog";
import type { LocalModifier, LocalVariant } from "../../data/local/db";
import { uuidv7 } from "../../platform/id";
import {
  saveCartSoon,
  flushCart,
  restoreCart,
  clearPersistedCart,
} from "../../data/local/cart-persistence";

/**
 * Cart state.
 *
 * Everything here is synchronous and in-memory. There is no await on the path
 * between tapping an item and the total changing — no network, no IndexedDB,
 * no query. That is Principle V's sub-100 ms budget being met by construction
 * rather than by optimisation, and it is why the catalog is indexed in memory
 * before the till renders.
 *
 * Pricing is recomputed from scratch on every change. That sounds wasteful and
 * is not: it is a pure function over a handful of lines, and it removes the
 * entire class of bug where an incrementally-updated total drifts from the
 * lines it claims to sum.
 */

export interface CartLine extends CartLineInput {
  /** Kept so the sheet can re-open with the same selections. */
  selectedModifierIds: string[];
}

export interface UseCartResult {
  lines: CartLine[];
  priced: PricedCart;
  /**
   * Exactly the inputs `priced` was computed from, discount included.
   *
   * Finalizing must use THESE, not `lines`. The promotional discount is
   * allocated onto the lines during pricing, so committing the raw lines would
   * record a sale at full price while the customer was shown, and charged, the
   * discounted total.
   */
  pricingInputs: CartLineInput[];
  isEmpty: boolean;
  addItem(view: CatalogItemView, variant?: LocalVariant | null, modifiers?: LocalModifier[]): void;
  setQuantity(lineId: string, quantity: number): void;
  increment(lineId: string): void;
  decrement(lineId: string): void;
  setNote(lineId: string, note: string | null): void;
  remove(lineId: string): void;
  clear(): void;
  replaceAll(lines: CartLine[]): void;

  /** The promotional discount in force, or null. */
  promo: PromoDiscount | null;
  /** Apply a percentage off the whole order. Requires an authorizer. Clears any statutory discount (FR-032). */
  applyPromo(rateBp: number, authorizedBy: string): void;
  removePromo(): void;

  /** The statutory (Senior Citizen / PWD) discount in force, or null. */
  statutory: StatutoryDiscount | null;
  /** The eligible amount this order's statutory discount, if any, is computed against. */
  statutoryResult: StatutoryDiscountResult | null;
  /** Apply a statutory discount. Requires an authorizer. Clears any promotional discount (FR-032). */
  applyStatutory(input: StatutoryDiscount): void;
  removeStatutory(): void;

  /**
   * Line grants (specs/006-loyalty contracts/core-extensions.md §1): lines a
   * module has given one unit of away, by line id. Only enabled modules'.
   */
  grants: Readonly<Record<string, LineGrantState>>;
  /** Each line's discount, split into its grant and its promo share. */
  discountParts: LineDiscountPart[];
  /**
   * Give one unit of a line away. A line of quantity > 1 is split first and
   * the new single-unit line is granted. Returns the granted line's id.
   */
  grantLine(moduleId: string, lineId: string, label: string): string;
  /** Remove a grant this module made. */
  revokeGrant(moduleId: string, lineId: string): void;

  /** Cart annotations (§2): one opaque value per module, cleared with the cart. */
  annotations: Readonly<Record<string, unknown>>;
  annotate(moduleId: string, value: unknown): void;
}

export interface LineGrantState {
  moduleId: string;
  label: string;
}

/** A percentage off the order, and who allowed it. */
export interface PromoDiscount {
  rateBp: number;
  /** The manager who approved it — recorded on every discount row. */
  authorizedBy: string;
}

/** A Senior Citizen / PWD discount, captured per FR-029. */
export interface StatutoryDiscount {
  discountType: "senior_citizen" | "pwd";
  cardholderName: string;
  cardholderId: string;
  coveredCustomers: number;
  totalCustomers: number;
  /** The manager who approved it — recorded on every discount row. */
  authorizedBy: string;
}

export function useCart(
  tax: TaxConfig,
  statutoryRateBp: number,
  shiftId: string | null = null,
): UseCartResult {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [promo, setPromo] = useState<PromoDiscount | null>(null);
  const [statutory, setStatutory] = useState<StatutoryDiscount | null>(null);
  const [rawGrants, setGrants] = useState<Record<string, LineGrantState>>({});
  const [rawAnnotations, setAnnotations] = useState<Record<string, unknown>>({});

  // Read synchronously by grantLine (which must return the granted id) and by
  // addItem (which must never merge a fresh tap into a free line).
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const grantsRef = useRef(rawGrants);
  grantsRef.current = rawGrants;

  /** A grant is lost the moment its line's quantity changes or the line goes. */
  const dropGrant = useCallback((lineId: string) => {
    setGrants((current) => {
      if (!(lineId in current)) return current;
      const { [lineId]: _gone, ...rest } = current;
      return rest;
    });
  }, []);

  // Mutually exclusive (order-discounts.ts, FR-032): applying either one
  // clears the other, mirroring DiscountDialog's existing one-slot model.
  const applyPromo = useCallback((rateBp: number, authorizedBy: string) => {
    setStatutory(null);
    setPromo({ rateBp, authorizedBy });
  }, []);
  const removePromo = useCallback(() => setPromo(null), []);

  const applyStatutory = useCallback((next: StatutoryDiscount) => {
    setPromo(null);
    setStatutory(next);
  }, []);
  const removeStatutory = useCallback(() => setStatutory(null), []);

  // Restore whatever the previous session was in the middle of (FR-044). Runs
  // once, and only adopts the stored cart if the in-memory one is still empty —
  // a restore that raced a fast first tap must not overwrite it.
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    let cancelled = false;

    void restoreCart(shiftId).then((stored) => {
      if (cancelled) return;
      if (stored) {
        setLines((current) => (current.length === 0 ? (stored as CartLine[]) : current));
      }
      setRestored(true);
    });

    return () => {
      cancelled = true;
    };
  }, [shiftId]);

  // Shadow the cart to IndexedDB. Fire-and-forget and debounced, so nothing on
  // the tap-to-total path ever awaits a write.
  //
  // Gated on `restored` so the initial empty state cannot delete the very cart
  // being recovered before the read returns.
  useEffect(() => {
    if (!restored) return;
    saveCartSoon(lines, shiftId);
  }, [lines, shiftId, restored]);

  // A deliberate close is the one moment worth paying for a synchronous-ish
  // flush, because there is no later.
  useEffect(() => {
    const flush = () => void flushCart();
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, []);

  const addItem = useCallback(
    (view: CatalogItemView, variant?: LocalVariant | null, modifiers: LocalModifier[] = []) => {
      const chosen = variant ?? view.defaultVariant;
      if (!chosen) return;

      const selectedModifierIds = modifiers.map((m) => m.id).sort();

      setLines((current) => {
        // Merge with an identical line rather than stacking duplicates — three
        // taps of the same drink should read "3 ×", not three rows to scan.
        const match = current.find(
          (line) =>
            !(line.lineId in grantsRef.current) &&
            line.menuItemId === view.item.id &&
            line.variantId === chosen.id &&
            line.note === null &&
            line.selectedModifierIds.length === selectedModifierIds.length &&
            line.selectedModifierIds.every((id, i) => id === selectedModifierIds[i]),
        );

        if (match) {
          return current.map((line) =>
            line.lineId === match.lineId ? { ...line, quantity: line.quantity + 1 } : line,
          );
        }

        return [
          ...current,
          {
            lineId: uuidv7(),
            menuItemId: view.item.id,
            variantId: chosen.id,
            itemName: view.item.name,
            // An item with one variant has no size to report. "Brownies" plus
            // a variant label is noise on the cart line, the ticket and the
            // receipt, so the name carries the whole meaning on its own.
            variantName: view.variants.length > 1 ? chosen.name : null,
            unitPrice: chosen.price,
            quantity: 1,
            modifiers: modifiers.map((m) => ({
              id: m.id,
              name: m.name,
              priceAdjustment: m.priceAdjustment,
            })),
            statutoryEligible: view.item.statutoryDiscountEligible,
            note: null,
            selectedModifierIds,
          },
        ];
      });
    },
    [],
  );

  const setQuantity = useCallback((lineId: string, quantity: number) => {
    dropGrant(lineId);
    setLines((current) =>
      quantity < 1
        ? current.filter((line) => line.lineId !== lineId)
        : current.map((line) => (line.lineId === lineId ? { ...line, quantity } : line)),
    );
  }, [dropGrant]);

  const increment = useCallback((lineId: string) => {
    dropGrant(lineId);
    setLines((current) =>
      current.map((line) =>
        line.lineId === lineId ? { ...line, quantity: line.quantity + 1 } : line,
      ),
    );
  }, [dropGrant]);

  const decrement = useCallback((lineId: string) => {
    dropGrant(lineId);
    setLines((current) =>
      current.flatMap((line) => {
        if (line.lineId !== lineId) return [line];
        // Decrementing past 1 removes the line. Nothing is ever left at zero,
        // which is what keeps the journal's "quantity >= 1" invariant reachable.
        return line.quantity <= 1 ? [] : [{ ...line, quantity: line.quantity - 1 }];
      }),
    );
  }, [dropGrant]);

  const setNote = useCallback((lineId: string, note: string | null) => {
    setLines((current) =>
      current.map((line) => (line.lineId === lineId ? { ...line, note } : line)),
    );
  }, []);

  const remove = useCallback((lineId: string) => {
    dropGrant(lineId);
    setLines((current) => current.filter((line) => line.lineId !== lineId));
  }, [dropGrant]);

  // Clearing is deliberate — a completed sale or an abandoned order — so the
  // stored copy goes with it rather than waiting for the debounce.
  const clear = useCallback(() => {
    setLines([]);
    setGrants({});
    setAnnotations({});
    void clearPersistedCart();
  }, []);
  const replaceAll = useCallback((next: CartLine[]) => {
    setGrants({});
    setLines(next);
  }, []);

  const grantLine = useCallback((moduleId: string, lineId: string, label: string): string => {
    if (lineId in grantsRef.current) throw new GrantError(`Line ${lineId} is already granted`);
    const { lines: next, grantedLineId } = splitLineForGrant(linesRef.current, lineId, uuidv7());
    linesRef.current = next;
    grantsRef.current = { ...grantsRef.current, [grantedLineId]: { moduleId, label } };
    setLines(next);
    setGrants(grantsRef.current);
    return grantedLineId;
  }, []);

  const revokeGrant = useCallback((moduleId: string, lineId: string) => {
    if (grantsRef.current[lineId]?.moduleId !== moduleId) return;
    dropGrant(lineId);
  }, [dropGrant]);

  const annotate = useCallback((moduleId: string, value: unknown) => {
    setAnnotations((current) => {
      if (value === null || value === undefined) {
        const { [moduleId]: _gone, ...rest } = current;
        return rest;
      }
      return { ...current, [moduleId]: value };
    });
  }, []);

  // A module switched off mid-order takes its grants and its context with it:
  // nothing it gave away may be finalized without it.
  const grants = useMemo(() => {
    const kept: Record<string, LineGrantState> = {};
    for (const [lineId, grant] of Object.entries(rawGrants)) {
      if (modules.isEnabled(grant.moduleId) && lines.some((line) => line.lineId === lineId)) {
        kept[lineId] = grant;
      }
    }
    return kept;
  }, [rawGrants, lines]);

  const annotations = useMemo(() => {
    const kept: Record<string, unknown> = {};
    for (const [moduleId, value] of Object.entries(rawAnnotations)) {
      if (modules.isEnabled(moduleId)) kept[moduleId] = value;
    }
    return kept;
  }, [rawAnnotations]);

  /**
   * Pricing with the promotional discount spread over the lines.
   *
   * The allocation happens HERE rather than at finalize so the cashier sees the
   * discounted line totals while the customer is still standing there, and so
   * the figures on screen are the same figures that get recorded. See
   * promo-discount.ts for why it is spread rather than subtracted at the end.
   */
  const discountParts = useMemo(
    () => lineDiscountParts(lines, new Set(Object.keys(grants)), promo?.rateBp ?? null),
    [lines, grants, promo],
  );

  // Grants come off first; a promo is allocated over what is left
  // (line-grant.ts). With neither in force, the raw lines are the inputs.
  const pricingInputs = useMemo<CartLineInput[]>(() => {
    if ((!promo && Object.keys(grants).length === 0) || lines.length === 0) return lines;
    return withLineDiscounts(lines, discountParts);
  }, [lines, grants, promo, discountParts]);

  /**
   * The statutory discount's figures, recomputed whenever the cart or the
   * discount itself changes — same "recompute from scratch, never
   * incrementally" philosophy as `priced` below (see this file's module
   * doc). Needs the ELIGIBLE line total, which is only known after pricing
   * the raw lines once with no discount in force — a second, cheap
   * `priceCart` call, not the one that becomes `priced`.
   */
  const statutoryResult = useMemo<StatutoryDiscountResult | null>(() => {
    if (!statutory || lines.length === 0) return null;

    // Promo and statutory never coexist (FR-032), so with a statutory discount
    // in force `pricingInputs` carries grants alone: a free item contributes
    // only what is still charged on it to the eligible base.
    const basePriced = priceCart(pricingInputs, tax);
    const eligibleAmount = sum(
      basePriced.lines.filter((l) => l.statutoryEligible).map((l) => l.lineTotal),
    );

    return computeStatutoryDiscount({
      eligibleAmount,
      coveredCustomers: statutory.coveredCustomers,
      totalCustomers: statutory.totalCustomers,
      tax,
      statutoryRateBp,
    });
  }, [statutory, lines, pricingInputs, tax, statutoryRateBp]);

  const priced = useMemo(
    () =>
      statutoryResult
        ? priceCart(pricingInputs, tax, { statutoryDiscount: statutoryResult })
        : priceCart(pricingInputs, tax),
    [lines, pricingInputs, tax, statutoryResult],
  );

  // A discount belongs to the order it was granted for. Leaving it applied
  // after the cart is cleared would silently give it to the next customer.
  const clearWithPromo = useCallback(() => {
    setPromo(null);
    setStatutory(null);
    clear();
  }, [clear]);

  return {
    lines,
    priced,
    pricingInputs,
    isEmpty: lines.length === 0,
    addItem,
    setQuantity,
    increment,
    decrement,
    setNote,
    remove,
    clear: clearWithPromo,
    replaceAll,
    promo,
    applyPromo,
    removePromo,
    statutory,
    statutoryResult,
    applyStatutory,
    removeStatutory,
    grants,
    discountParts,
    grantLine,
    revokeGrant,
    annotations,
    annotate,
  };
}
